import { useState, useEffect, useCallback } from 'react';
import { Compass, Loader2, RefreshCcw, Sparkles, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';
import { vocab } from '@/lib/projectVocab';

const URGENCY_STYLE = {
  high:   { dot: 'bg-red-500',    label: 'text-red-700 dark:text-red-300',    bg: 'bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900' },
  medium: { dot: 'bg-amber-500',  label: 'text-amber-700 dark:text-amber-300',bg: 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900' },
  low:    { dot: 'bg-emerald-500',label: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900' },
};

/**
 * AICoachCard — Sprint 5+ workflow guidance.
 *
 * Aggregates per-chapter signals (words/evidence/citations/grounding/compliance)
 * → ranks readiness → recommends a small set of concrete next actions.
 * Clicking an action jumps to the relevant chapter.
 */
export default function AICoachCard({ userId, projectId, projectType, onJumpToChapter }) {
  const v = vocab(projectType);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async (withLlm = false) => {
    if (!userId || !projectId) return;
    setLoading(true);
    try {
      const res = await thesisApi.coachRecommendations(userId, projectId, withLlm, 'en');
      setData(res);
    } catch (e) {
      toast.error(`Coach failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId]);

  useEffect(() => { load(false); }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex items-center gap-2 text-xs text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading AI Coach…
      </div>
    );
  }
  if (!data) return null;

  const overall = data.overall_readiness_pct ?? 0;
  const overallClass = overall >= 75 ? 'text-emerald-600 dark:text-emerald-400' : overall >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="rounded-xl border border-blue-200 dark:border-blue-900 bg-gradient-to-br from-blue-50/60 via-white to-emerald-50/40 dark:from-blue-950/30 dark:via-slate-950 dark:to-emerald-950/20 shadow-sm overflow-hidden" data-testid="ai-coach-card">
      <button type="button" onClick={() => setExpanded(!expanded)} className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-blue-50/40 dark:hover:bg-blue-950/20 transition" data-testid="ai-coach-toggle">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
          <Compass className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-serif font-semibold text-slate-900 dark:text-white" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            AI Coach · what to work on next
          </p>
          <p className="text-[11px] text-slate-600 dark:text-slate-400 truncate">
            {data.coach_summary || `Overall readiness ${overall}% · ${data.recommendations?.length || 0} actions queued`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className={`text-2xl font-bold ${overallClass}`}>{overall}<span className="text-xs text-slate-400">%</span></div>
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {/* Progress bar */}
          <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden mb-3">
            <div className={`h-full transition-all ${overall >= 75 ? 'bg-emerald-500' : overall >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${overall}%` }} />
          </div>

          {/* Recommendations */}
          {data.recommendations?.length ? (
            <div className="space-y-1.5" data-testid="coach-recommendations">
              {data.recommendations.map((r, i) => {
                const style = URGENCY_STYLE[r.urgency] || URGENCY_STYLE.medium;
                return (
                  <button key={i} type="button"
                    onClick={() => onJumpToChapter?.(r.chapter_id)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-md border ${style.bg} hover:shadow-sm transition group`}
                    data-testid={`coach-recommendation-${i}`}>
                    <span className={`h-2 w-2 rounded-full ${style.dot} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">
                        <span className={`uppercase tracking-widest text-[9px] mr-1 ${style.label}`}>{r.urgency}</span>
                        {r.action}
                      </p>
                      <p className="text-[10px] text-slate-600 dark:text-slate-400 truncate">
                        <strong>{r.chapter_title}</strong> · {r.why}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-slate-500 flex-shrink-0">
                      <span className="font-mono">{r.readiness_score}%</span>
                      <span>· ~{r.est_minutes}m</span>
                      <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition" />
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-900 px-3 py-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <p className="text-xs text-emerald-800 dark:text-emerald-200">All {v.section}s look healthy. Consider running a {v.panel} for final rehearsal.</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-blue-100 dark:border-blue-950">
            <Button size="sm" variant="ghost" onClick={() => load(true)} disabled={loading}
              className="text-xs text-slate-600 dark:text-slate-300"
              data-testid="coach-refresh-btn">
              {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCcw className="h-3 w-3 mr-1" />}
              Refresh with AI summary
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
